import consola from "consola"
import type { TWebphoneMessage, TWebphoneMessageAction } from "@/schemas/webphone.schema"

export type ChromeExtensionControlAction = "getExtensionInfo" | "getSidePanelState" | "openSidePanel" | "closeSidePanel"
export type ChromeExtensionAction = ChromeExtensionControlAction | TWebphoneMessageAction
export type WebphoneParentAction = "openMessenger"
export type ChromeExtensionReadinessFailureReason = "not_installed" | "not_ready" | "runtime_unavailable"

export type ChromeExtensionReadinessResult = { ok: true; state: "ready"; openedSidePanel: boolean } | { ok: false; reason: ChromeExtensionReadinessFailureReason; lastState: string | null; openedSidePanel: boolean }
export interface ChromeExtensionInfo {
  response: unknown
  version: string | null
}

export interface ChromeExtensionMessage {
  from: "ringio-chrome-app"
  action: ChromeExtensionAction
  data: Record<string, unknown>
}

export interface WebphoneParentMessage {
  from: "ringio-webphone"
  action: WebphoneParentAction
  data: Record<string, unknown>
}

export interface ChromeExtensionRuntimeLike {
  sendMessage: (extensionId: string, message: ChromeExtensionMessage) => Promise<unknown> | unknown
}

export interface EnsureChromeExtensionReadyOptions {
  pollIntervalMs?: number
  timeoutMs?: number
  sleep?: (ms: number) => Promise<void>
  onAwaitingReady?: () => void
}

const DEFAULT_READY_POLL_INTERVAL_MS = 250
const DEFAULT_READY_TIMEOUT_MS = 30000
const extensionLogger = consola.withTag("chrome-extension")

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })
}

function isChromeExtensionStateResponse(value: unknown): value is { state: string } {
  return Boolean(value && typeof value === "object" && "state" in value && typeof value.state === "string")
}

function isChromeExtensionDataStateResponse(value: unknown): value is { data: string } {
  return Boolean(value && typeof value === "object" && "data" in value && typeof value.data === "string")
}

function getChromeExtensionVersion(value: unknown): string | null {
  if (!value || typeof value !== "object") return null

  if ("version" in value && typeof value.version === "string") {
    return value.version
  }

  if ("data" in value && value.data && typeof value.data === "object" && "version" in value.data && typeof value.data.version === "string") {
    return value.data.version
  }

  return null
}

function isSidePanelOpenButNotReady(state: string | null) {
  return state === "open"
}

export function buildChromeExtensionMessage(action: ChromeExtensionAction, data: Record<string, unknown> = {}): ChromeExtensionMessage {
  return {
    from: "ringio-chrome-app",
    action,
    data,
  }
}

export function getWebphoneParentMessage(action: WebphoneParentAction, data: Record<string, unknown> = {}): WebphoneParentMessage {
  return {
    from: "ringio-webphone",
    action,
    data,
  }
}

export function isChromeExtensionRuntimeAvailable(runtime: unknown): runtime is ChromeExtensionRuntimeLike {
  return Boolean(runtime && typeof (runtime as ChromeExtensionRuntimeLike).sendMessage === "function")
}

export function getChromeExtensionRuntime(source: { chrome?: { runtime?: unknown } } = globalThis as { chrome?: { runtime?: unknown } }): ChromeExtensionRuntimeLike | null {
  const runtime = source.chrome?.runtime
  if (!isChromeExtensionRuntimeAvailable(runtime)) return null

  return runtime
}

export function getChromeExtensionInstallUrl(extensionId: string | null | undefined): string | null {
  if (!extensionId) return null

  return `https://chromewebstore.google.com/detail/ringio-web-phone/${extensionId}`
}

export async function sendChromeExtensionMessage(extensionId: string | null | undefined, action: ChromeExtensionAction, data: Record<string, unknown> = {}, runtime = getChromeExtensionRuntime()): Promise<unknown> {
  if (!extensionId || !runtime) {
    extensionLogger.warn("sendChromeExtensionMessage aborted", {
      action,
      hasExtensionId: Boolean(extensionId),
      hasRuntime: Boolean(runtime),
    })
    throw new Error("Chrome extension runtime is not available")
  }

  extensionLogger.debug("Sending Chrome extension message", {
    extensionId,
    action,
    data,
  })

  const response = await runtime.sendMessage(extensionId, buildChromeExtensionMessage(action, data))

  extensionLogger.debug("Received Chrome extension response", {
    extensionId,
    action,
    response,
  })

  return response
}

export async function sendChromeExtensionWebphoneMessage(extensionId: string | null | undefined, message: TWebphoneMessage, runtime = getChromeExtensionRuntime()): Promise<unknown> {
  extensionLogger.info("Sending webphone command through the Chrome extension runtime", {
    extensionId,
    action: message.action,
    data: message.data,
  })

  return sendChromeExtensionMessage(extensionId, message.action, message.data, runtime)
}

export async function checkChromeExtensionInstalled(extensionId: string | null | undefined, runtime = getChromeExtensionRuntime()): Promise<boolean> {
  const extensionInfo = await getChromeExtensionInfo(extensionId, runtime)

  if (extensionInfo) {
    extensionLogger.info("Chrome extension installation check succeeded", {
      extensionId,
      version: extensionInfo.version,
    })
    return true
  }

  return false
}

export async function getChromeExtensionInfo(extensionId: string | null | undefined, runtime = getChromeExtensionRuntime()): Promise<ChromeExtensionInfo | null> {
  try {
    const response = await sendChromeExtensionMessage(extensionId, "getExtensionInfo", {}, runtime)

    return {
      response,
      version: getChromeExtensionVersion(response),
    }
  } catch (error) {
    extensionLogger.warn("Chrome extension info request failed", {
      extensionId,
      error,
    })
    return null
  }
}

export async function getChromeExtensionSidePanelState(extensionId: string | null | undefined, runtime = getChromeExtensionRuntime()): Promise<string | null> {
  try {
    const response = await sendChromeExtensionMessage(extensionId, "getSidePanelState", {}, runtime)

    if (typeof response === "string") {
      extensionLogger.info("Chrome extension side panel state resolved from string response", {
        extensionId,
        state: response,
      })
      return response
    }

    if (isChromeExtensionStateResponse(response)) {
      extensionLogger.info("Chrome extension side panel state resolved from object response", {
        extensionId,
        state: response.state,
      })
      return response.state
    }

    if (isChromeExtensionDataStateResponse(response)) {
      extensionLogger.info("Chrome extension side panel state resolved from data response", {
        extensionId,
        state: response.data,
      })
      return response.data
    }

    extensionLogger.warn("Chrome extension side panel state response had an unexpected shape", {
      extensionId,
      response,
    })
    return null
  } catch (error) {
    extensionLogger.warn("Chrome extension side panel state request failed", {
      extensionId,
      error,
    })
    return null
  }
}

export async function openChromeExtensionSidePanelBestEffort(extensionId: string | null | undefined, runtime = getChromeExtensionRuntime()): Promise<boolean> {
  try {
    await sendChromeExtensionMessage(extensionId, "openSidePanel", {}, runtime)
    extensionLogger.info("Requested Chrome extension side panel open", {
      extensionId,
    })
    return true
  } catch (error) {
    extensionLogger.warn("Failed to request Chrome extension side panel open", {
      extensionId,
      error,
    })
    return false
  }
}

export async function ensureChromeExtensionReady(extensionId: string | null | undefined, runtime = getChromeExtensionRuntime(), options: EnsureChromeExtensionReadyOptions = {}): Promise<ChromeExtensionReadinessResult> {
  const { pollIntervalMs = DEFAULT_READY_POLL_INTERVAL_MS, timeoutMs = DEFAULT_READY_TIMEOUT_MS, sleep = delay, onAwaitingReady } = options

  extensionLogger.info("Starting Chrome extension readiness check", {
    extensionId,
    hasRuntime: Boolean(runtime),
    pollIntervalMs,
    timeoutMs,
  })

  if (!runtime) {
    extensionLogger.warn("Chrome extension readiness failed because runtime is unavailable", {
      extensionId,
    })
    return {
      ok: false,
      reason: "runtime_unavailable",
      lastState: null,
      openedSidePanel: false,
    }
  }

  if (!extensionId) {
    extensionLogger.warn("Chrome extension readiness failed because extension id is missing")
    return {
      ok: false,
      reason: "not_installed",
      lastState: null,
      openedSidePanel: false,
    }
  }

  const isInstalled = await checkChromeExtensionInstalled(extensionId, runtime)
  if (!isInstalled) {
    extensionLogger.warn("Chrome extension readiness failed because installation check did not succeed", {
      extensionId,
    })
    return {
      ok: false,
      reason: "not_installed",
      lastState: null,
      openedSidePanel: false,
    }
  }

  let lastState = await getChromeExtensionSidePanelState(extensionId, runtime)
  extensionLogger.info("Chrome extension readiness initial side panel state", {
    extensionId,
    state: lastState,
  })
  if (lastState === "ready") {
    extensionLogger.info("Chrome extension already ready", {
      extensionId,
    })
    return {
      ok: true,
      state: "ready",
      openedSidePanel: false,
    }
  }

  let openedSidePanel = false

  if (isSidePanelOpenButNotReady(lastState)) {
    extensionLogger.info("Chrome extension side panel is already open; waiting for readiness", {
      extensionId,
      lastState,
    })
  } else {
    openedSidePanel = await openChromeExtensionSidePanelBestEffort(extensionId, runtime)
    if (!openedSidePanel) {
      extensionLogger.warn("Chrome extension readiness could not open the side panel", {
        extensionId,
        lastState,
      })
      return {
        ok: false,
        reason: "not_ready",
        lastState,
        openedSidePanel: false,
      }
    }
  }

  onAwaitingReady?.()

  const deadline = Date.now() + timeoutMs
  let pollAttempt = 0

  while (Date.now() < deadline) {
    await sleep(pollIntervalMs)
    pollAttempt += 1
    lastState = await getChromeExtensionSidePanelState(extensionId, runtime)

    extensionLogger.debug("Chrome extension readiness polling attempt", {
      extensionId,
      pollAttempt,
      state: lastState,
    })

    if (lastState === "ready") {
      extensionLogger.info("Chrome extension became ready after opening the side panel", {
        extensionId,
        pollAttempt,
      })
      return {
        ok: true,
        state: "ready",
        openedSidePanel,
      }
    }
  }

  extensionLogger.warn("Chrome extension readiness timed out before the side panel became ready", {
    extensionId,
    pollAttempt,
    lastState,
  })

  return {
    ok: false,
    reason: "not_ready",
    lastState,
    openedSidePanel,
  }
}

export function postWebphoneParentMessage(action: WebphoneParentAction, targetWindow: Window = window.top ?? window): void {
  targetWindow.postMessage(getWebphoneParentMessage(action), "*")
}
